import React, { useEffect, useMemo, useState } from "react";
import theme from "assets/theme";
import FaiTypography from "MuiComponents/FaiTypography";
import { Box, Card, CircularProgress } from "@mui/material";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel } from "@mui/x-data-grid";
import Axios from "services/api/config";
import { getAllRefundsURL } from "services/api/routes/Order";
import dataTableData from "./data/dataTableData";

interface RefundRow {
	id: string;
	productName: string;
	amount: string;
	refundType: string;
	destination: string;
	status: string;
	date: string;
	raw: any;
}

// Human-readable label for where the refund money went.
const destinationLabel = (d?: string) => {
	switch (d) {
		case "WALLET":
			return "Wallet";
		case "ORIGINAL_PAYMENT":
			return "Original payment";
		case "BANK":
			return "Bank";
		case "UPI":
			return "UPI";
		default:
			return "-";
	}
};

// Friendly label for the cancellation/refund type.
const typeLabel = (t?: string) => {
	switch (t) {
		case "RETURN_ITEM":
			return "Return";
		case "CANCEL_ITEM":
			return "Item cancel";
		case "CANCEL_SUBORDER":
			return "Order cancel";
		default:
			return t || "-";
	}
};

// Flatten a backend RefundResponseDTO into a flat table row.
const toRow = (r: any): RefundRow => ({
	id: r?.id,
	productName: r?.productName || "-",
	amount: `₹ ${r?.amount ?? 0}`,
	refundType: typeLabel(r?.refundType),
	destination: destinationLabel(r?.refundDestination),
	status: r?.status || "-",
	date: r?.createdAt ? new Date(r.createdAt).toLocaleDateString() : "-",
	raw: r,
});

function Refunds() {
	const { typography } = theme;
	const [refunds, setRefunds] = useState<RefundRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	const getRefunds = async () => {
		try {
			setLoading(true);
			const { data } = await Axios({
				...getAllRefundsURL({
					page: paginationModel.page,
					size: paginationModel.pageSize,
				}),
			});
			const responseData = data?.data || {};
			setRefunds((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error(error);
			setRefunds([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getRefunds();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize]);

	// Client-side filter over the loaded page (the backend endpoint takes no
	// search parameter), matching product name or status.
	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return refunds;
		return refunds.filter(
			(r) =>
				r.productName.toLowerCase().includes(q) ||
				r.status.toLowerCase().includes(q)
		);
	}, [refunds, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	return (
		<Card className="my-3 p-3 md:p-5">
			<FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
				Refunds
			</FaiTypography>
			<p className="text-sm mb-4">
				All refunds across the platform — cancellations and returns. Each row
				shows the amount, where the money was credited, and its processing
				status.
			</p>
			<Box className="flex flex-col md:flex-row justify-between items-center my-4 w-full gap-3">
				<SearchInput
					placeholder="Search By Product / Status"
					value={search}
					onChange={(e: any) => setSearch(e.target.value)}
				/>
			</Box>
			<Box sx={{ mt: 2 }}>
				{loading ? (
					<Box className="flex justify-center p-6">
						<CircularProgress />
					</Box>
				) : (
					<FaiDataGrid
						rows={visibleRows}
						columns={dataTableData.columns}
						pagination={true}
						pageSize={paginationModel.pageSize}
						rowCount={rowCount}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
					/>
				)}
			</Box>
		</Card>
	);
}

export default Refunds;
